import { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { supabase } from '@/integrations/supabase/client'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { useToast } from '@/hooks/use-toast'
import { Heart, Users, Gift, Loader2 } from 'lucide-react'
import { useAuth } from '@/hooks/useAuth'
import { trackReferralUsage } from '@/utils/tenantReferralUtils'

const ReferralPage = () => {
  const { code, landingPage } = useParams<{ code: string; landingPage?: string }>()
  const navigate = useNavigate()
  const { toast } = useToast()
  const { user } = useAuth()
  const [loading, setLoading] = useState(true)
  const [referrerInfo, setReferrerInfo] = useState<any>(null)
  const [registering, setRegistering] = useState(false)
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    fullName: ''
  })

  useEffect(() => {
    if (user && !landingPage) {
      // User is already logged in and no specific landing page, redirect to dashboard
      navigate('/kunde')
      return
    }
    
    if (code) {
      // Track the referral link access with landing page info
      trackReferralUsage(code, 'direct_link', navigator.userAgent, { landingPage })
      validateReferralCode()
    }
  }, [code, user, landingPage])

  const validateReferralCode = async () => {
    try {
      const { data: referrer, error } = await supabase
        .from('customers')
        .select('id, full_name, email, visit_stamps, referral_stamps, tenant_id')
        .eq('referral_code', code)
        .single()

      if (error || !referrer) {
        toast({
          variant: "destructive",
          title: "Ungültiger Empfehlungslink",
          description: "Dieser Empfehlungslink ist nicht gültig oder abgelaufen.",
        })
        navigate('/')
        return
      }

      // Track the specific referrer for this session
      sessionStorage.setItem('referrer_id', referrer.id)
      sessionStorage.setItem('referral_code', code!)
      if (landingPage) {
        sessionStorage.setItem('landing_page', landingPage)
      }

      setReferrerInfo(referrer)
    } catch (error) {
      console.error('Error validating referral code:', error)
      toast({
        variant: "destructive",
        title: "Fehler",
        description: "Empfehlungslink konnte nicht validiert werden.",
      })
      navigate('/')
    } finally {
      setLoading(false)
    }
  }

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!formData.email || !formData.password || !formData.fullName) {
      toast({
        variant: "destructive",
        title: "Fehler",
        description: "Bitte füllen Sie alle Felder aus.",
      })
      return
    }

    setRegistering(true)

    try {
      // Sign up the new user
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email: formData.email,
        password: formData.password,
        options: {
          emailRedirectTo: `${window.location.origin}/customer`,
          data: {
            full_name: formData.fullName
          }
        }
      })

      if (authError) throw authError

      if (authData.user) {
        // Process the referral with enhanced tracking
        const { error: referralError } = await supabase.functions.invoke('enhanced-referral-process', {
          body: {
            referralCode: code,
            newUserEmail: formData.email,
            newUserId: authData.user.id,
            newUserName: formData.fullName,
            landingPage: landingPage || null,
            referrerInfo: {
              id: referrerInfo?.id,
              name: referrerInfo?.full_name,
              tenant_id: referrerInfo?.tenant_id
            }
          }
        })

        if (referralError) {
          console.error('Referral processing error:', referralError)
          // Still continue with registration even if referral fails
        }

        toast({
          title: "Registrierung erfolgreich!",
          description: `Willkommen! ${referrerInfo?.full_name || 'Der Empfehler'} erhält einen Empfehlungsstempel.`,
        })

        // Redirect after successful registration
        setTimeout(() => {
          navigate('/kunde')
        }, 2000)
      }
    } catch (error: any) {
      console.error('Registration error:', error)
      toast({
        variant: "destructive",
        title: "Registrierung fehlgeschlagen",
        description: error.message || "Ein Fehler ist aufgetreten.",
      })
    } finally {
      setRegistering(false)
    }
  }

  const handleInputChange = (field: keyof typeof formData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }))
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="flex items-center gap-2">
          <Loader2 className="h-4 w-4 animate-spin" />
          <p>Empfehlungslink wird validiert...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary/5 via-background to-accent/5 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Referrer Info Card */}
        {referrerInfo && (
          <Card className="mb-6 border-primary/20 bg-gradient-to-r from-primary/5 to-accent/5">
            <CardHeader className="text-center">
              <div className="mx-auto mb-2 p-3 bg-gradient-to-r from-primary to-accent rounded-full w-fit">
                <Users className="h-6 w-6 text-white" />
              </div>
              <CardTitle className="text-lg">
                {referrerInfo.full_name || referrerInfo.email} lädt Sie ein!
              </CardTitle>
              <CardDescription>
                Registrieren Sie sich und beide erhalten Vorteile
              </CardDescription>
            </CardHeader>
            
            <CardContent>
              <div className="grid grid-cols-2 gap-4 text-center">
                <div className="p-3 bg-background/50 rounded-lg">
                  <Heart className="h-5 w-5 text-primary mx-auto mb-1" />
                  <p className="text-sm font-medium">{referrerInfo.visit_stamps}</p>
                  <p className="text-xs text-muted-foreground">Besuche</p>
                </div>
                <div className="p-3 bg-background/50 rounded-lg">
                  <Gift className="h-5 w-5 text-accent mx-auto mb-1" />
                  <p className="text-sm font-medium">{referrerInfo.referral_stamps}</p>
                  <p className="text-xs text-muted-foreground">Empfehlungen</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Registration Form */}
        <Card>
          <CardHeader>
            <CardTitle>Jetzt registrieren</CardTitle>
            <CardDescription>
              Erstellen Sie Ihr Konto und sammeln Sie digitale Stempel
            </CardDescription>
          </CardHeader>
          
          <CardContent>
            <form onSubmit={handleRegister} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="fullName">Vollständiger Name</Label>
                <Input
                  id="fullName"
                  value={formData.fullName}
                  onChange={(e) => handleInputChange('fullName', e.target.value)}
                  placeholder="Ihr vollständiger Name"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="email">E-Mail-Adresse</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  placeholder="ihre@email.de"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="password">Passwort</Label>
                <Input
                  id="password"
                  type="password"
                  value={formData.password}
                  onChange={(e) => handleInputChange('password', e.target.value)}
                  placeholder="Mindestens 6 Zeichen"
                  required
                  minLength={6}
                />
              </div>

              <div className="bg-accent/10 p-3 rounded-lg border border-accent/20">
                <div className="flex items-center gap-2 text-sm">
                  <Gift className="h-4 w-4 text-accent" />
                  <span className="font-medium">Empfehlungsbonus:</span>
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  Durch Ihre Registrierung erhält {referrerInfo?.full_name || 'der Empfehler'} einen Empfehlungsstempel!
                </p>
              </div>

              <Button 
                type="submit" 
                className="w-full" 
                disabled={registering}
              >
                {registering ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Wird registriert...
                  </>
                ) : (
                  'Kostenlos registrieren'
                )}
              </Button>
            </form>

            <div className="mt-6 text-center">
              <p className="text-sm text-muted-foreground">
                Bereits ein Konto?{' '}
                <Button 
                  variant="link" 
                  className="p-0 h-auto"
                  onClick={() => navigate('/anmelden')}
                >
                  Hier anmelden
                </Button>
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}

export default ReferralPage